import { Button, cx, LoadingIndicator } from "@light-technology/components";
import { toPlainText, type PortableTextBlock } from "@portabletext/react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  ArrowRight,
  MessageChatCircle,
  SearchMd,
  Stars01,
} from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { api } from "../../state/api";
import {
  FeatureFlag,
  type PlaybookContentFaqSearchResult,
  type PlaybookContentGuideSearchResult,
} from "../../state/api.types";
import { chat } from "../../state/chat";
import { useAuthHasFeatureFlag } from "../../state/hooks";
import { useTypedDispatch } from "../../state/store";
import { getChatSuggestions } from "../../utilities/chat";
import { playbookFAQLink, playbookGuideLink } from "../../utilities/links";
import { useDebounce } from "../../utilities/useDebounce";
import styles from "./PlaybookSearchHero.module.css";

type SearchResult =
  PlaybookContentGuideSearchResult | PlaybookContentFaqSearchResult;

const resultUrl = (result: SearchResult) =>
  "guide" in result
    ? playbookGuideLink(result.guide)
    : playbookFAQLink(result.faq);

export const PlaybookSearchHero = () => {
  const dispatch = useTypedDispatch();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions] = useState(() => getChatSuggestions(5));
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const linkRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const hasChat = useAuthHasFeatureFlag(FeatureFlag.Chat);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  const debouncedQuery = useDebounce(query, 300);
  const { data: searchData, isFetching } = api.useGetPlaybookContentSearchQuery(
    debouncedQuery.trim() || skipToken,
  );

  const isSearching =
    isFetching ||
    (query.trim() !== "" && query.trim() !== debouncedQuery.trim());

  const results: SearchResult[] =
    // Only show results over a certain relevance score. No special reason about
    // the number, just dialed in based on feeling after testing a few queries.
    searchData?.results.filter((r) => r.score > 6.5) ?? [];

  const showDropdown = isOpen && query.trim() !== "";

  const closeDropdown = () => {
    setQuery("");
    setIsOpen(false);
    setActiveIndex(-1);
  };

  // Drive result selection through simulating a click of the link DOM element
  const clickResult = (index: number) => {
    linkRefs.current[index]?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setIsOpen(true);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < results.length) {
        // User explicitly arrowed to a result — go there.
        clickResult(activeIndex);
      } else if (hasChat && query.trim()) {
        // No explicit selection — open chat with the query.
        dispatch(chat.actions.openWithQuestion(query.trim()));
        closeDropdown();
      } else if (results.length > 0) {
        // No chat available — fall back to the first result.
        clickResult(0);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="relative flex flex-col items-center justify-center py-32">
      <div className="relative flex w-full max-w-lg flex-col items-center gap-4">
        <p className="w-full text-center text-display-xs leading-8 text-primary">
          Welcome to <strong className="font-semibold">Playbook</strong>, your
          guide to winning with energy. What do you want to learn about today?
        </p>

        <div className="flex w-full max-w-sm flex-col items-center gap-5">
          <div
            ref={containerRef}
            className={cx(styles.searchContainer, "relative z-2 w-full")}
          >
            <div
              aria-hidden="true"
              className={cx(
                styles.searchShadow,
                "absolute left-0 h-12 w-full rounded-full bg-brand-accent-magenta mix-blend-multiply",
              )}
              style={{ "--offset": "4px" } as React.CSSProperties}
            />
            <div
              aria-hidden="true"
              className={cx(
                styles.searchShadow,
                "absolute left-0 h-12 w-full rounded-full bg-brand-accent-cyan mix-blend-multiply",
              )}
              style={{ "--offset": "8px" } as React.CSSProperties}
            />
            <div
              aria-hidden="true"
              className={cx(
                styles.searchShadow,
                "absolute left-0 h-12 w-full rounded-full bg-brand-accent-yellow mix-blend-multiply",
              )}
              style={{ "--offset": "12px" } as React.CSSProperties}
            />
            <div className="relative flex rounded-full border border-primary bg-primary">
              <div className="flex flex-1 items-center gap-2 overflow-clip rounded-l-full border-t border-b border-l border-transparent bg-primary px-3.5 py-2.5">
                {hasChat ? (
                  <Stars01 className="size-5 shrink-0 text-tertiary" />
                ) : (
                  <SearchMd className="size-5 shrink-0 text-tertiary" />
                )}
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  data-ph-capture-attribute-action="playbook-search-input"
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    setIsOpen(true);
                  }}
                  placeholder={
                    hasChat
                      ? "Search or ask a question"
                      : "Search guides and FAQs"
                  }
                  className="min-w-0 flex-1 bg-transparent text-base text-primary outline-none placeholder:text-placeholder"
                />
              </div>
              <button
                type="button"
                className="flex items-center justify-center self-stretch rounded-r-full p-3"
                onClick={() => {
                  clickResult(activeIndex >= 0 ? activeIndex : 0);
                }}
              >
                <ArrowRight className="size-5 text-primary" />
              </button>
            </div>

            {showDropdown && (
              <div className="absolute right-0 left-0 mt-1 flex max-h-64 flex-col rounded-lg border border-secondary bg-primary shadow-lg">
                <div className="scrollbar-custom min-h-0 flex-1 overflow-auto py-1">
                  {isSearching ? (
                    <div className="flex flex-1 items-center justify-center py-3">
                      <LoadingIndicator type="brand-spinner" />
                    </div>
                  ) : results.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center py-6 text-md text-disabled">
                      No results found
                    </div>
                  ) : (
                    results.map((result, i) => {
                      const key =
                        "guide" in result ? result.guide.slug : result.faq.slug;
                      const title =
                        "guide" in result
                          ? result.guide.title
                          : result.faq.question;
                      const subtitle =
                        "guide" in result
                          ? result.guide.tagline
                          : toPlainText(
                              result.faq.answer as PortableTextBlock[],
                            );
                      return (
                        <div key={key} className="px-1.5 py-px">
                          <Link
                            ref={(el) => {
                              linkRefs.current[i] = el;
                            }}
                            to={resultUrl(result)}
                            data-ph-capture-attribute-action="playbook-search-result"
                            className={`flex w-full flex-col rounded-md p-2 text-left transition-colors ${
                              i === activeIndex
                                ? "bg-secondary_hover"
                                : "hover:bg-secondary_hover"
                            }`}
                          >
                            <span className="text-base leading-6 font-medium text-primary">
                              {title}
                            </span>
                            <span className="truncate text-base leading-6 text-tertiary">
                              {subtitle}
                            </span>
                          </Link>
                        </div>
                      );
                    })
                  )}
                </div>
                {hasChat && (
                  <div className="shrink-0 border-t border-secondary">
                    <button
                      type="button"
                      className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3.5 py-2.5 text-left transition-colors hover:bg-secondary_hover"
                      data-ph-capture-attribute-action="playbook-search-ask"
                      onClick={() => {
                        dispatch(chat.actions.openWithQuestion(query));
                        closeDropdown();
                      }}
                    >
                      <MessageChatCircle className="size-5 shrink-0 text-tertiary" />
                      <span className="text-base leading-6 font-medium text-primary">
                        Ask Playbook:
                      </span>
                      <span className="text-base leading-6 text-tertiary">
                        &ldquo;{query}&rdquo;
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {hasChat && (
            <div className="relative z-1 mx-[-32px] scrollbar-hide flex w-[calc(100%+32px)] gap-2 overflow-auto mask-[linear-gradient(to_right,transparent_0,black_2rem,black_calc(100%-2rem),transparent)] px-4">
              {suggestions.map((s) => (
                <Button
                  key={s}
                  color="secondary"
                  size="sm"
                  data-ph-capture-attribute-action="playbook-search-suggestion"
                  onClick={() => {
                    dispatch(chat.actions.openWithSuggestion(s));
                  }}
                  className="font-sans text-xs"
                >
                  {s}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
